import { useId, useMemo, useRef, useState, useLayoutEffect } from 'react';
import { cn } from '$lib/lib/cn';
import { formatDate, formatKr } from '$lib/lib/formatters';

export interface SparkPoint {
  ts: string; // ISO date
  priceKrPerMonth: number;
}

export interface SparklineProps {
  data: SparkPoint[];
  width?: number;
  height?: number;
  className?: string;
  ariaLabel?: string;
}

// Inline 80×16 sparkline. Pure SVG, no library. Reads token colors via CSS
// vars so it works under both themes without re-renders. Hover tooltip
// surfaces the latest observation date + price (decision 12).
export function Sparkline({
  data,
  width = 80,
  height = 16,
  className,
  ariaLabel
}: SparklineProps) {
  const id = useId();
  const [hover, setHover] = useState<{ x: number; y: number; idx: number } | null>(null);
  const svgRef = useRef<SVGSVGElement | null>(null);
  // Flip the tooltip below the sparkline when the row is near the top of the
  // viewport (under the sticky table header), and clamp horizontally when the
  // sparkline is at the edge of the viewport (last column / narrow window).
  // Recomputed on hover entry so it tracks the row's current scroll position.
  const [placement, setPlacement] = useState<'above' | 'below'>('above');
  const [hAlign, setHAlign] = useState<'center' | 'left' | 'right'>('center');
  useLayoutEffect(() => {
    if (!hover || !svgRef.current) return;
    const rect = svgRef.current.getBoundingClientRect();
    setPlacement(rect.top < 60 ? 'below' : 'above');
    const vw = window.innerWidth;
    const TOOLTIP_HALF = 80; // approx half-width of the tooltip
    const center = rect.left + rect.width / 2;
    if (center + TOOLTIP_HALF > vw - 8) setHAlign('right');
    else if (center - TOOLTIP_HALF < 8) setHAlign('left');
    else setHAlign('center');
  }, [hover]);

  const { path, points, last, min, max, trend } = useMemo(() => {
    if (data.length === 0) {
      return { path: '', points: [], last: null, min: 0, max: 0, trend: 0 };
    }
    const values = data.map((d) => d.priceKrPerMonth);
    const min = Math.min(...values);
    const max = Math.max(...values);
    const range = max - min || 1;
    const stepX = data.length === 1 ? 0 : width / (data.length - 1);
    const pad = 1;
    const inner = height - pad * 2;
    const pts = data.map((d, i) => {
      const x = i * stepX;
      const y = pad + (1 - (d.priceKrPerMonth - min) / range) * inner;
      return { x, y, d };
    });
    const path = pts
      .map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x.toFixed(2)} ${p.y.toFixed(2)}`)
      .join(' ');
    const last = data[data.length - 1];
    const trend = data.length > 1 ? data[data.length - 1].priceKrPerMonth - data[0].priceKrPerMonth : 0;
    return { path, points: pts, last, min, max, trend };
  }, [data, width, height]);

  if (data.length === 0) {
    return <span className={cn('inline-block text-fg-subtle text-2xs', className)}>—</span>;
  }

  const stroke = trend > 0 ? 'rgb(var(--color-spark-up))' : trend < 0 ? 'rgb(var(--color-spark-down))' : 'rgb(var(--color-spark))';

  function onMove(e: React.MouseEvent<SVGSVGElement>) {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const idx = Math.max(0, Math.min(points.length - 1, Math.round((x / width) * (points.length - 1))));
    setHover({ x: points[idx].x, y: points[idx].y, idx });
  }

  return (
    <span className={cn('relative inline-block align-middle', className)}>
      <svg
        ref={svgRef}
        role="img"
        aria-label={ariaLabel ?? `Price trend, latest ${formatKr(last?.priceKrPerMonth ?? 0)} on ${formatDate(last?.ts ?? null)}`}
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        onMouseMove={onMove}
        onMouseLeave={() => setHover(null)}
        className="block cursor-crosshair"
      >
        <defs>
          <linearGradient id={`spark-fill-${id}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={stroke} stopOpacity="0.18" />
            <stop offset="100%" stopColor={stroke} stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d={`${path} L ${width} ${height} L 0 ${height} Z`} fill={`url(#spark-fill-${id})`} />
        <path d={path} fill="none" stroke={stroke} strokeWidth={1.25} strokeLinejoin="round" strokeLinecap="round" />
        {hover && <circle cx={hover.x} cy={hover.y} r={1.6} fill={stroke} />}
      </svg>
      {hover && (
        <span
          role="tooltip"
          className={cn(
            'pointer-events-none absolute z-50 whitespace-nowrap rounded-sm border border-rule bg-surface px-2 py-1 text-2xs shadow-elev-2',
            placement === 'above' ? 'bottom-[110%]' : 'top-[110%]',
            hAlign === 'center' && 'left-1/2 -translate-x-1/2',
            hAlign === 'left' && 'left-0',
            hAlign === 'right' && 'right-0'
          )}
        >
          <span className="num text-fg">{formatKr(points[hover.idx].d.priceKrPerMonth)}</span>
          <span className="ml-2 text-fg-muted">{formatDate(points[hover.idx].d.ts)}</span>
        </span>
      )}
      <span className="sr-only">
        Range {formatKr(min)} to {formatKr(max)}
      </span>
    </span>
  );
}
